import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import { CustomTableFilter } from '@components/table-filters/CustomTableFilter';
import { useDeleteReport } from '@hooks/deleteQueries';
import { useReports } from '@hooks/getQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import StudyReportForm from './StudyReportForm';

const useStyles = makeStyles(() => ({
  actionButton: {
    cursor: 'pointer',
  },
}));

const MemoizedMenuItem = React.memo(MenuItem);

function ReportList({ studyType, canManageReport = true }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const classes = useStyles();
  const [openForm, setOpenForm] = useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    limit: 10,
    offset: 0,
    study_type: studyType,
  });
  const { data: reports } = useReports(paginationModel, id);
  const [editData, setEditData] = useState(null);
  const { mutateAsync: deleteReport, isPending: isReportDeleting } = useDeleteReport();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['reports'] });
    if (state?.showAddToReportModal) {
      setOpenForm(true);
    }
  }, [paginationModel, queryClient]);

  const handleClick = useCallback((event, study) => {
    setAnchorEl({ target: event.currentTarget, id: study.id }); // Set the DOM element as anchor
  }, []);

  const handleCloseForm = () => {
    navigate(
      `/${studyType === 'dynamic_survey' ? 'dynamic-survey' : 'interview'}/${id}/custom-reports`,
      {
        replace: true,
        state: {
          showAddToReportModal: false,
        },
      },
    );
    setAnchorEl(null);
  };

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null); // Close the menu
  }, []);

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography
            variant="body2"
            onClick={() =>
              navigate(
                `/${studyType === 'dynamic_survey' ? 'dynamic-survey' : 'interview'}/${id}/custom-reports/${params.row.id}`,
              )
            }
            className={classes.actionButton}
            sx={{ fontWeight: 500 }}
          >
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    { field: 'created_by', headerName: 'Created By', width: 200 },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 130,
      renderCell: (params) => (
        <span>
          {params?.row?.created_at ? new Date(params?.row?.created_at).toLocaleDateString() : '--'}
        </span>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 190,
      renderCell: (params) => (
        <span>
          {params?.row?.updated_at ? new Date(params?.row?.updated_at).toLocaleDateString() : '--'}
        </span>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 90,
      sortable: false,
      align: 'right',
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        // Don't render action column for users without management permissions
        if (!canManageReport) return null;
        return (
          <Box>
            <IconButton
              aria-label="More options"
              className={classes.actionButton}
              onClick={(event) => handleClick(event, params.row)} // Pass row ID and event
            >
              <MoreHorizIcon />
            </IconButton>
            {/* Menu for Actions */}
            <Menu
              anchorEl={anchorEl?.target} // Use anchorEl as the DOM element
              open={Boolean(anchorEl?.target) && anchorEl.id === params.row.id} // Ensure only the clicked row menu opens
              onClose={handleCloseMenu}
              component={'div'}
              sx={{
                boxShadow: 'none',
                '& .MuiPaper-root': {
                  p: 1,
                },
                '& .MuiMenuItem-root': {
                  borderRadius: '6px',
                },
              }}
              elevation={0}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MemoizedMenuItem
                onClick={() => {
                  setEditData(params.row);
                  setOpenForm(true);
                }}
              >
                {' '}
                Edit
              </MemoizedMenuItem>
              <MemoizedMenuItem
                onClick={() =>
                  show(() =>
                    deleteReport({
                      studyId: id,
                      reportId: params.row.id,
                    }),
                  )
                }
                sx={{ color: 'error.main' }}
              >
                Delete
              </MemoizedMenuItem>
            </Menu>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <CustomTableFilter
        data={reports}
        columns={columns}
        isPending={false}
        setPaginationModel={setPaginationModel}
        paginationModel={paginationModel}
        noRowsTitle={!canManageReport ? 'No Reports Available' : 'No Reports Found'}
        noRowsText={
          !canManageReport
            ? 'You currently have view-only access'
            : 'Get started by creating a new Report'
        }
        filterToolbar={
          <Box display={'flex'} alignItems={'center'} pt={3} justifyContent={'space-between'}>
            <Typography variant="h6">Reports</Typography>
            {canManageReport && (
              <Button variant="contained" onClick={() => setOpenForm(true)}>
                Create Report
              </Button>
            )}
          </Box>
        }
      />

      <Dialog open={openForm} fullWidth onClose={handleCloseForm} maxWidth="xs" sx={{ p: 0 }}>
        <DialogContent sx={{ p: 0 }}>
          <Grid container>
            <Grid item xs={12} borderLeft={1} borderColor={'#D9D9D9'}>
              <StudyReportForm setOpen={setOpenForm} studyType={studyType} editData={editData} />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isPending={isReportDeleting}
        title={'Delete Report'}
        content={'Are you sure you want to delete this report?'}
        okText={isReportDeleting ? 'Deleting...' : 'Delete Report'}
      />
    </>
  );
}

export default ReportList;
